// useSnapshotPoll — periodic snapshot polling for the Cameras wall.
//
// Per LLD §8.3 / 09 §8.2 row 7 + the product decision (option c, owner-
// confirmed): per-tile previews on the wall are still images, NOT live
// WHEP. Live WebRTC stays focused-tile (CameraDetail) so we don't
// breach AVA's per-device 30s/60s session caps or the global 100 TPS
// budget when 16+ tiles are visible.
//
// Behavior summary (matches the prompt's Step 2 contract):
//   - Mount: schedule first fetch with a small randomized lead so
//     a wall full of tiles doesn't fire all at once.
//   - Each tick: walk through the global concurrency semaphore (cap
//     ~4); if the semaphore is full, drop this tick and let the next
//     interval retry (queue depth stays bounded by design).
//   - Errors: 403 → status "no-access", stop polling. 404 / 5xx /
//     network → retry on the next interval.
//   - Visibility: pause polling while document.visibilityState ===
//     "hidden" (matches WhepPlayer.tsx). Resume on visible.
//   - Unmount: clear timer, abort in-flight fetch, revoke any blob
//     URLs we created so the browser releases the JPEG memory.
//
// Testability: timers, fetch, and visibility source are injectable so
// the test suite can drive them deterministically without jsdom.

import { useEffect, useRef, useState, type RefObject } from "react";
import {
  fetchSnapshot as defaultFetchSnapshot,
  type SnapshotFetchResult,
} from "@/api/snapshotClient";
import {
  snapshotSemaphore as defaultSemaphore,
  type Priority,
  type Semaphore,
} from "./snapshotPollSemaphore";
import type { ViewportPriority } from "./viewportPriority";

export type SnapshotPollStatus =
  | "loading"
  | "ok"
  | "no-access"
  | "no-preview"
  | "error";

export interface UseSnapshotPollOptions {
  deviceId: string;
  /**
   * If false, the hook is inert — no fetch, no timer, no listeners —
   * and `status` stays at "loading" forever. Useful for tiles whose
   * catalog status already says the device is offline; saves the BFF
   * a guaranteed-404 round trip per poll.
   */
  enabled?: boolean;
  /** Mean polling interval. Default 45s — actual interval jitters by ±jitterMs. */
  intervalMs?: number;
  /** Half-width of the jitter window. Default 15s, so [30s, 60s]. */
  jitterMs?: number;
  /**
   * Maximum delay before the first fetch on mount, to spread the wall's
   * initial load. Default 5s.
   */
  initialDelayMaxMs?: number;
  /** Test seam — RNG used for jitter. Default Math.random. */
  randomSource?: () => number;
  /** Test seam — fetcher. Default the snapshotClient.fetchSnapshot impl. */
  fetcher?: (
    deviceId: string,
    signal?: AbortSignal,
  ) => Promise<SnapshotFetchResult>;
  /** Test seam — concurrency limiter. Default the module-scoped one. */
  semaphore?: Semaphore;
  /**
   * Live viewport-priority signal driven by IntersectionObserver
   * (see {@link useViewportPriority}). When provided:
   *   - "visible" → fetch with semaphore priority "high"
   *   - "near"    → fetch with semaphore priority "normal" (prefetch)
   *   - "far"     → defer entirely; no network until the tile gets
   *                 closer to the viewport.
   *
   * Pass the React-state value (re-renders trigger a wake-up edge so
   * a far→near transition kicks off a fetch immediately) OR the ref
   * (read inside the loop). When both are passed, both work together
   * as designed by {@link useViewportPriority}.
   *
   * Omit to opt out of viewport gating — every tick runs visible/high.
   */
  priorityRef?: RefObject<ViewportPriority>;
  /**
   * Optional state-form of the viewport priority. When this changes
   * from "far" to "near" or "visible", the hook fires an immediate
   * extra fetch instead of waiting for the next jittered interval.
   * Cheap when no transition occurs.
   */
  priority?: ViewportPriority;
}

export interface UseSnapshotPollResult {
  src: string | null;
  status: SnapshotPollStatus;
  /**
   * Wall-clock timestamp (ms) of the last successful snapshot fetch,
   * or null when no successful fetch has completed yet. Drives the
   * tile's "10s ago" age label so operators don't mistake the
   * still-image for a live stream.
   */
  lastUpdatedAt: number | null;
  /** Trigger a fresh fetch immediately (best-effort; respects the semaphore). */
  refresh(): void;
}

const DEFAULT_INTERVAL_MS = 45_000;
const DEFAULT_JITTER_MS = 15_000;
// Short cold-start lead: with the JSON-URL poster contract, the first
// fetch is cheap (one BFF + one CDN GET), so we don't need a 5s smear
// to keep CPU happy. 600ms still spreads tile starts enough that the
// semaphore queue drains cleanly while the wall populates fast.
const DEFAULT_INITIAL_DELAY_MAX_MS = 600;

/**
 * Compute the next-tick delay. Exposed so tests can verify the jitter
 * stays inside [interval - jitter, interval + jitter].
 */
export function computeNextDelayMs(
  intervalMs: number,
  jitterMs: number,
  rng: () => number,
): number {
  // rng() in [0, 1) → offset in [-jitter, +jitter)
  const offset = (rng() * 2 - 1) * jitterMs;
  // Floor at 1s so a pathological RNG can't busy-loop the timer.
  return Math.max(1_000, intervalMs + offset);
}

/**
 * Compute the initial mount delay. Exposed for tests so we can assert
 * it's bounded by [0, initialDelayMaxMs).
 */
export function computeInitialDelayMs(
  initialDelayMaxMs: number,
  rng: () => number,
): number {
  return Math.floor(rng() * initialDelayMaxMs);
}

export function useSnapshotPoll(
  opts: UseSnapshotPollOptions,
): UseSnapshotPollResult {
  const {
    deviceId,
    enabled = true,
    intervalMs = DEFAULT_INTERVAL_MS,
    jitterMs = DEFAULT_JITTER_MS,
    initialDelayMaxMs = DEFAULT_INITIAL_DELAY_MAX_MS,
    randomSource = Math.random,
    fetcher = defaultFetchSnapshot,
    semaphore = defaultSemaphore,
    priorityRef,
    priority,
  } = opts;

  const [src, setSrc] = useState<string | null>(null);
  const [status, setStatus] = useState<SnapshotPollStatus>("loading");
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);

  // Refs so timer callbacks always see the latest values without
  // resubscribing the poll loop on every render.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const stoppedRef = useRef<boolean>(false);
  // Pointer the public refresh() can call to trigger a tick from the
  // current effect's closure. Re-assigned on each effect run.
  const runOnceRef = useRef<() => void>(() => {});

  // The bag of latest deps each tick reads — mutating refs avoids
  // re-creating the timer-driven loop on every prop change.
  const optsRef = useRef({
    intervalMs,
    jitterMs,
    initialDelayMaxMs,
    randomSource,
    fetcher,
    semaphore,
    priorityRef,
    priority,
  });
  optsRef.current = {
    intervalMs,
    jitterMs,
    initialDelayMaxMs,
    randomSource,
    fetcher,
    semaphore,
    priorityRef,
    priority,
  };

  useEffect(() => {
    stoppedRef.current = false;
    setStatus("loading");
    setSrc(null);
    setLastUpdatedAt(null);

    if (!enabled) {
      // Inert — caller opted out (e.g. catalog already says offline).
      // Mark stopped so the runOnceRef can never wake the poll up via
      // refresh().
      stoppedRef.current = true;
      runOnceRef.current = () => {};
      return () => {
        // Nothing to tear down.
      };
    }

    function clearTimer(): void {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }

    let firstFetchPending = true;

    function viewportPriority(): ViewportPriority {
      const ref = optsRef.current.priorityRef;
      if (ref && ref.current != null) return ref.current;
      const propVal = optsRef.current.priority;
      if (propVal != null) return propVal;
      // Caller didn't wire viewport priority — treat the tile as visible
      // so the existing un-prioritized behavior is preserved.
      return "visible";
    }

    function semaphorePriority(vp: ViewportPriority): Priority {
      if (vp === "visible") return "high";
      if (vp === "near") return "normal";
      return "low";
    }

    async function runOnce(): Promise<void> {
      if (stoppedRef.current) return;
      // Don't poll while hidden. Visibility-resume schedules its own
      // fresh tick, so we just defer until then.
      if (
        typeof document !== "undefined" &&
        document.visibilityState === "hidden"
      ) {
        scheduleNext();
        return;
      }

      // Far tiles defer entirely — no network, no semaphore traffic.
      // The viewport observer will call refresh() (via its own effect
      // in the consumer) when the tile re-enters near/visible.
      if (viewportPriority() === "far") {
        scheduleNext();
        return;
      }

      const sem = optsRef.current.semaphore;
      const prio = semaphorePriority(viewportPriority());
      let release: (() => void) | null;
      if (firstFetchPending) {
        // First fetch: queue rather than drop, so every tile populates
        // even when the wall mounts hot. The cold-start delay smear
        // already keeps the queue shallow. Priority gates ordering.
        release = await sem.acquire(prio);
        firstFetchPending = false;
      } else {
        // Steady-state: drop if the semaphore is full at our priority —
        // the next jittered interval will retry, keeping queue depth
        // bounded.
        release = sem.tryAcquire(prio);
        if (release === null) {
          scheduleNext();
          return;
        }
      }

      // Re-check stopped after the (possibly awaited) acquire.
      if (stoppedRef.current) {
        release();
        return;
      }

      const ac = new AbortController();
      abortRef.current = ac;

      let result: SnapshotFetchResult;
      try {
        result = await optsRef.current.fetcher(deviceId, ac.signal);
      } catch {
        result = { kind: "error", status: 0 };
      } finally {
        release();
        if (abortRef.current === ac) abortRef.current = null;
      }

      if (stoppedRef.current) {
        return;
      }

      switch (result.kind) {
        case "ok": {
          setSrc(result.url);
          setStatus("ok");
          setLastUpdatedAt(Date.now());
          scheduleNext();
          return;
        }
        case "no-access": {
          setSrc(null);
          setLastUpdatedAt(null);
          setStatus("no-access");
          // Stop polling — permission isn't going to flip mid-session
          // without a role change, and a role change remounts the page.
          stoppedRef.current = true;
          return;
        }
        case "no-preview": {
          setSrc(null);
          setLastUpdatedAt(null);
          setStatus("no-preview");
          // Camera might come online — keep polling.
          scheduleNext();
          return;
        }
        case "error": {
          // Don't drop the previous successful image on a transient
          // error — leave src in place but flip status to "error" so
          // the tile can overlay an indicator if it wants to.
          setStatus("error");
          scheduleNext();
          return;
        }
      }
    }

    function scheduleNext(): void {
      if (stoppedRef.current) return;
      clearTimer();
      const delay = computeNextDelayMs(
        optsRef.current.intervalMs,
        optsRef.current.jitterMs,
        optsRef.current.randomSource,
      );
      timerRef.current = setTimeout(() => {
        void runOnce();
      }, delay);
    }

    // Wire up the public refresh() pointer.
    runOnceRef.current = (): void => {
      clearTimer();
      void runOnce();
    };

    // Initial schedule — a small random lead in [0, initialDelayMaxMs)
    // so 16 tiles mounting at the same time don't all fire at t=0.
    const initialDelay = computeInitialDelayMs(
      optsRef.current.initialDelayMaxMs,
      optsRef.current.randomSource,
    );
    timerRef.current = setTimeout(() => {
      void runOnce();
    }, initialDelay);

    function onVisibility(): void {
      if (stoppedRef.current) return;
      if (document.visibilityState === "visible") {
        // Force a fresh tick now so operators get a current image when
        // they return to the tab. Drop any timer that fired while
        // hidden (it would've been a no-op anyway).
        clearTimer();
        void runOnce();
      }
    }

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }

    return () => {
      stoppedRef.current = true;
      clearTimer();
      if (abortRef.current !== null) {
        try {
          abortRef.current.abort();
        } catch {
          // ignore
        }
      }
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
      // Reset the runOnce ref so a stale refresh() call after unmount
      // doesn't wake the dead.
      runOnceRef.current = () => {};
    };
    // We re-run on deviceId / enabled change. intervalMs etc. are read
    // via optsRef so new values take effect on the next tick without
    // tearing down the loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, enabled]);

  // Wake-on-viewport-entry: when the tile transitions from "far" to
  // "near" or "visible", fire a refresh now instead of waiting for the
  // next jittered interval (which can be ~30s out). The previous-value
  // ref ensures we only wake on the leading edge, not on every render.
  const prevPriorityRef = useRef<ViewportPriority | undefined>(priority);
  useEffect(() => {
    const prev = prevPriorityRef.current;
    prevPriorityRef.current = priority;
    if (!enabled) return;
    if (priority == null) return;
    if (prev === "far" && (priority === "near" || priority === "visible")) {
      runOnceRef.current();
    }
  }, [priority, enabled]);

  function refresh(): void {
    runOnceRef.current();
  }

  return { src, status, lastUpdatedAt, refresh };
}
